import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
  Grid,
  IconButton,
  Avatar,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { getDriverProfilePictureUrl } from '../../utils/s3Utils';

const ManagerDrivers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [driverStatusFilter, setDriverStatusFilter] = useState('all');
  
  // Vehicle assignment dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedDriverForAssign, setSelectedDriverForAssign] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');
  
  // Driver edit dialog
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [driverDetailDialogOpen, setDriverDetailDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverFormData, setDriverFormData] = useState({
    name: '',
    phone_number: '',
    status: 'idle',
    current_vehicle: '',
    license_number: '',
    license_expiration_date: '',
    license_state: '',
    license_class: '',
    license_verified: false,
    license_verification_notes: '',
  });
  const [driverError, setDriverError] = useState('');
  const [driverSuccess, setDriverSuccess] = useState('');

  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [grantTargetDriver, setGrantTargetDriver] = useState(null);
  const [grantRole, setGrantRole] = useState('customer');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantError, setGrantError] = useState('');
  const [grantSuccess, setGrantSuccess] = useState('');
  const [grantForm, setGrantForm] = useState({
    company_name: '',
    phone_number: '',
    address: '',
    name: '',
    license_number: '',
    license_expiration_date: '',
    license_state: '',
    license_class: '',
  });

  const portalRoleLabels = { customer: 'Customer', driver: 'Driver', manager: 'Manager' };

  useEffect(() => {
    fetchDrivers();
    fetchVehicles();
  }, []);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/managers/drivers/');
      setDrivers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/vehicles/');
      let vehiclesData = [];
      if (Array.isArray(response.data)) {
        vehiclesData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        vehiclesData = response.data.results;
      }
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    }
  };

  const handleOpenGrantDialog = (driver) => {
    const roles = driver.user?.accessible_roles || [];
    const needCustomer = !roles.includes('customer');
    const needDriver = !roles.includes('driver');
    setGrantTargetDriver(driver);
    setGrantRole(needCustomer ? 'customer' : needDriver ? 'driver' : 'customer');
    setGrantError('');
    setGrantSuccess('');
    setGrantForm({
      company_name: '',
      phone_number: driver.phone_number || '',
      address: '',
      name: driver.name || '',
      license_number: driver.license_number || '',
      license_expiration_date: driver.license_expiration_date || '',
      license_state: driver.license_state || '',
      license_class: driver.license_class || '',
    });
    setGrantDialogOpen(true);
  };

  const handleCloseGrantDialog = () => {
    setGrantDialogOpen(false);
    setGrantTargetDriver(null);
    setGrantError('');
    setGrantSuccess('');
  };

  const handleGrantPortalAccess = async () => {
    if (!grantTargetDriver?.user?.id) {
      setGrantError('Missing user account on this driver record.');
      return;
    }
    setGrantLoading(true);
    setGrantError('');
    setGrantSuccess('');
    try {
      const payload = {
        user_id: grantTargetDriver.user.id,
        role: grantRole,
      };
      if (grantRole === 'customer') {
        if (grantForm.company_name) payload.company_name = grantForm.company_name;
        if (grantForm.phone_number) payload.phone_number = grantForm.phone_number;
        if (grantForm.address) payload.address = grantForm.address;
      } else {
        payload.name = grantForm.name;
        payload.phone_number = grantForm.phone_number || '';
        payload.license_number = grantForm.license_number;
        payload.license_expiration_date = grantForm.license_expiration_date;
        payload.license_state = grantForm.license_state;
        payload.license_class = grantForm.license_class;
      }
      const res = await api.post('/managers/grant-portal-access/', payload);
      setGrantSuccess(res.data?.message || 'Access updated.');
      fetchDrivers();
      setTimeout(() => handleCloseGrantDialog(), 1500);
    } catch (err) {
      const d = err.response?.data;
      setGrantError(d?.error || d?.detail || 'Failed to grant access');
    } finally {
      setGrantLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      idle: 'success',
      active: 'primary',
      refueling: 'warning',
      not_working: 'error',
      license_suspended: 'error',
    };
    return colors[status] || 'default';
  };

  // Vehicle assignment handlers
  const handleOpenAssignDialog = async (driver) => {
    setSelectedDriverForAssign(driver);
    setSelectedVehicle(driver?.current_vehicle_info?.id || '');
    setAssignError('');
    setAssignSuccess('');
    await fetchVehicles();
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedDriverForAssign(null);
    setSelectedVehicle('');
    setAssignError('');
    setAssignSuccess('');
  };

  const handleAssignVehicle = async () => {
    if (!selectedDriverForAssign) return;

    setAssignError('');
    setAssignSuccess('');

    try {
      if (!selectedVehicle) {
        if (!selectedDriverForAssign.current_vehicle_info) {
          setAssignError('Driver has no vehicle assigned');
          return;
        }
        // Managers can unassign drivers from vehicles regardless of vehicle status
        await api.post('/managers/assign-vehicle/', {
          vehicle_id: selectedDriverForAssign.current_vehicle_info.id,
          driver_id: null,
        });
        setAssignSuccess('Vehicle unassigned successfully');
      } else {
        await api.post('/managers/assign-vehicle/', {
          vehicle_id: selectedVehicle,
          driver_id: selectedDriverForAssign.id,
        });
        setAssignSuccess('Vehicle assigned successfully');
      }
      
      fetchDrivers();
      fetchVehicles();
      setTimeout(() => {
        handleCloseAssignDialog();
      }, 1500);
    } catch (error) {
      console.error('Error assigning vehicle:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          'Failed to assign vehicle';
      setAssignError(errorMessage);
    }
  };

  const getAvailableVehicles = () => {
    if (!Array.isArray(vehicles) || vehicles.length === 0) {
      return [];
    }
    return vehicles.filter(vehicle => vehicle?.status !== 'out_of_service');
  };

  // Driver edit handlers
  const handleOpenDriverDialog = (driver = null) => {
    if (driver) {
      setSelectedDriver(driver);
      setDriverFormData({
        name: driver.name || '',
        phone_number: driver.phone_number || '',
        status: driver.status || 'idle',
        current_vehicle: driver.current_vehicle || driver.current_vehicle_info?.id || '',
        license_number: driver.license_number || '',
        license_expiration_date: driver.license_expiration_date || '',
        license_state: driver.license_state || '',
        license_class: driver.license_class || '',
        license_verified: driver.license_verified || false,
        license_verification_notes: driver.license_verification_notes || '',
      });
    } else {
      setSelectedDriver(null);
      setDriverFormData({
        name: '',
        phone_number: '',
        status: 'idle',
        current_vehicle: '',
        license_number: '',
        license_expiration_date: '',
        license_state: '',
        license_class: '',
        license_verified: false,
        license_verification_notes: '',
      });
    }
    setDriverDialogOpen(true);
    setDriverError('');
    setDriverSuccess('');
  };

  const handleCloseDriverDialog = () => {
    setDriverDialogOpen(false);
    setSelectedDriver(null);
    setDriverError('');
    setDriverSuccess('');
  };

  const handleDriverChange = (e) => {
    const { name, value } = e.target;
    setDriverFormData({
      ...driverFormData,
      [name]: value,
    });
    
    if (name === 'status' && (value === 'not_working' || value === 'license_suspended')) {
      setDriverFormData(prev => ({
        ...prev,
        current_vehicle: '',
      }));
    }
  };

  const handleDriverSubmit = async () => {
    setDriverError('');
    setDriverSuccess('');

    if (!driverFormData.name || !driverFormData.phone_number || 
        !driverFormData.license_number || !driverFormData.license_expiration_date ||
        !driverFormData.license_state || !driverFormData.license_class) {
      setDriverError('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        name: driverFormData.name,
        phone_number: driverFormData.phone_number,
        status: driverFormData.status,
        current_vehicle: driverFormData.current_vehicle || null,
        license_number: driverFormData.license_number,
        license_expiration_date: driverFormData.license_expiration_date,
        license_state: driverFormData.license_state,
        license_class: driverFormData.license_class,
        license_verified: driverFormData.license_verified,
        license_verification_notes: driverFormData.license_verification_notes || null,
      };

      if (selectedDriver) {
        await api.patch(`/drivers/${selectedDriver.id}/`, payload);
        setDriverSuccess('Driver updated successfully');
      } else {
        setDriverError('Cannot create drivers from this page. Please use registration.');
        return;
      }

      fetchDrivers();
      setTimeout(() => {
        handleCloseDriverDialog();
      }, 1500);
    } catch (error) {
      console.error('Error saving driver:', error);
      const errorData = error.response?.data;
      let errorMessage = 'Failed to save driver';
      
      if (errorData) {
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else {
          const firstError = Object.values(errorData)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      setDriverError(errorMessage);
    }
  };

  // Filter drivers
  const getFilteredDrivers = () => {
                  let filtered = [...drivers];
    
    if (driverSearchTerm) {
      const searchLower = driverSearchTerm.toLowerCase();
      filtered = filtered.filter(driver =>
        driver.name?.toLowerCase().includes(searchLower) ||
        driver.phone_number?.toLowerCase().includes(searchLower) ||
        driver.license_number?.toLowerCase().includes(searchLower)
      );
    }
    
    if (driverStatusFilter !== 'all') {
      filtered = filtered.filter(driver => driver.status === driverStatusFilter);
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const filteredDrivers = getFilteredDrivers();

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Drivers Management
        </Typography>

        {/* Search and Filter Controls */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search Drivers"
            placeholder="Search by name or phone..."
            value={driverSearchTerm}
            onChange={(e) => setDriverSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 250, flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={driverStatusFilter}
              onChange={(e) => setDriverStatusFilter(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="idle">Idle</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="refueling">Refueling</MenuItem>
              <MenuItem value="not_working">Not Working</MenuItem>
              <MenuItem value="license_suspended">License Suspended</MenuItem>
            </Select>
          </FormControl>
          {(driverSearchTerm || driverStatusFilter !== 'all') && (
            <Button
              variant="outlined"
              onClick={() => {
                setDriverSearchTerm('');
                setDriverStatusFilter('all');
              }}
              size="small"
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Drivers Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Photo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>License Info</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Current Job</TableCell>
                <TableCell>Portal access</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDrivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {drivers.length === 0 
                        ? 'No drivers found'
                        : 'No drivers match your search criteria'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDrivers.map((driver) => (
                  <TableRow key={driver.id}>
                    <TableCell>
                      <Avatar
                        src={getDriverProfilePictureUrl(driver.id, driver.profile_photo)}
                        alt={driver.name}
                        sx={{ width: 40, height: 40 }}
                      >
                        {driver.name?.charAt(0)?.toUpperCase() || 'D'}
                      </Avatar>
                    </TableCell>
                    <TableCell>{driver.name || 'N/A'}</TableCell>
                    <TableCell>{driver.phone_number || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={driver.status || 'unknown'}
                        color={getStatusColor(driver.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {driver.license_number ? (
                        <Box>
                          <Typography variant="caption" display="block">
                            {driver.license_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {driver.license_state} • {driver.license_class}
                          </Typography>
                          {driver.license_expiration_date && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Exp: {new Date(driver.license_expiration_date).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {driver.current_vehicle_info 
                        ? driver.current_vehicle_info.display || 
                          `${driver.current_vehicle_info.make} ${driver.current_vehicle_info.model} (${driver.current_vehicle_info.license_plate})`
                        : 'None'}
                    </TableCell>
                    <TableCell>
                      {driver.current_job_info
                        ? `${driver.current_job_info.job_location || 'N/A'} (${
                            driver.current_job_info.diesel_quantity > 0 ? `${driver.current_job_info.diesel_quantity}L Diesel` : ''
                          }${
                            driver.current_job_info.diesel_quantity > 0 && driver.current_job_info.def_quantity > 0 ? ' / ' : ''
                          }${
                            driver.current_job_info.def_quantity > 0 ? `${driver.current_job_info.def_quantity}L DEF` : ''
                          }${
                            (!driver.current_job_info.diesel_quantity || driver.current_job_info.diesel_quantity === 0) && 
                            (!driver.current_job_info.def_quantity || driver.current_job_info.def_quantity === 0) ? '0L' : ''
                          })`
                        : 'None'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 220 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(driver.user?.accessible_roles || []).map((r) => (
                            <Chip
                              key={r}
                              label={portalRoleLabels[r] || r}
                              size="small"
                              variant="outlined"
                              color={r === 'manager' ? 'secondary' : 'default'}
                            />
                          ))}
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<PersonAddIcon />}
                          onClick={() => handleOpenGrantDialog(driver)}
                          disabled={
                            (driver.user?.accessible_roles || []).includes('customer') &&
                            (driver.user?.accessible_roles || []).includes('driver')
                          }
                          sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                        >
                          Add access
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedDriver(driver);
                            setDriverDetailDialogOpen(true);
                          }}
                        >
                          View Details
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDriverDialog(driver)}
                          color="primary"
                          title="Edit driver"
                        >
                          <EditIcon />
                        </IconButton>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AssignmentIcon />}
                          onClick={() => handleOpenAssignDialog(driver)}
                          disabled={driver.status === 'not_working' || driver.status === 'license_suspended'}
                        >
                          {driver.current_vehicle_info ? 'Change Vehicle' : 'Assign Vehicle'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Vehicle Assignment Dialog */}
        <Dialog 
          open={assignDialogOpen} 
          onClose={handleCloseAssignDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            {selectedDriverForAssign?.current_vehicle_info ? 'Change Vehicle Assignment' : 'Assign Vehicle'}
            {selectedDriverForAssign && ` - ${selectedDriverForAssign.name}`}
          </DialogTitle>
          <DialogContent>
            {assignError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignError('')}>
                {assignError}
              </Alert>
            )}
            {assignSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAssignSuccess('')}>
                {assignSuccess}
              </Alert>
            )}
            {(selectedDriverForAssign?.status === 'not_working' || selectedDriverForAssign?.status === 'license_suspended') && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Cannot assign vehicle to driver with "{selectedDriverForAssign?.status === 'not_working' ? 'Not Working' : 'License Suspended'}" status
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Current Vehicle: {selectedDriverForAssign?.current_vehicle_info 
                  ? selectedDriverForAssign.current_vehicle_info.display || 
                    `${selectedDriverForAssign.current_vehicle_info.make} ${selectedDriverForAssign.current_vehicle_info.model} (${selectedDriverForAssign.current_vehicle_info.license_plate})`
                  : 'None'}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Vehicle</InputLabel>
                <Select
                  value={selectedVehicle}
                  onChange={(e) => setSelectedVehicle(e.target.value)}
                  label="Select Vehicle"
                  disabled={selectedDriverForAssign?.status === 'not_working' || selectedDriverForAssign?.status === 'license_suspended'}
                >
                  <MenuItem value="">
                    <em>Unassign Vehicle</em>
                  </MenuItem>
                  {getAvailableVehicles().length === 0 ? (
                    <MenuItem disabled>
                      {vehicles.length === 0 ? 'No vehicles found. Please create vehicles first.' : 'No available vehicles (all are out of service)'}
                    </MenuItem>
                  ) : (
                    getAvailableVehicles().map((vehicle) => {
                      const vehicleDriverId = typeof vehicle.current_driver === 'string' 
                        ? vehicle.current_driver 
                        : vehicle.current_driver?.id || vehicle.current_driver;
                      const driverId = selectedDriverForAssign?.id;
                      
                      const isAssignedToOther = vehicleDriverId && vehicleDriverId !== driverId;
                      const isAssignedToThis = vehicleDriverId === driverId;
                      
                      return (
                        <MenuItem key={vehicle?.id || Math.random()} value={vehicle?.id}>
                          {vehicle?.make || 'N/A'} {vehicle?.model || 'N/A'} ({vehicle?.license_plate || 'N/A'})
                          {vehicle?.status && ` - ${vehicle.status}`}
                          {isAssignedToThis && ' ✓ (Currently Assigned)'}
                          {isAssignedToOther && ' ⚠ (Assigned to Another Driver - Will Be Reassigned)'}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>
              {getAvailableVehicles().length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    <strong>Manager Assignment:</strong> You can assign any vehicle to any driver. 
                    If a vehicle is currently assigned to another driver, it will be automatically 
                    unassigned from that driver and assigned to the selected driver.
                  </Typography>
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignDialog}>Cancel</Button>
            <Button 
              onClick={handleAssignVehicle} 
              variant="contained"
              disabled={selectedDriverForAssign?.status === 'not_working' || selectedDriverForAssign?.status === 'license_suspended'}
            >
              {selectedVehicle ? 'Assign' : 'Unassign'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Driver Edit Dialog - Same as Dashboard */}
        <Dialog 
          open={driverDialogOpen} 
          onClose={handleCloseDriverDialog} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            {selectedDriver ? `Edit Driver - ${selectedDriver.name}` : 'Edit Driver'}
          </DialogTitle>
          <DialogContent>
            {driverError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDriverError('')}>
                {driverError}
              </Alert>
            )}
            {driverSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDriverSuccess('')}>
                {driverSuccess}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="name"
                    label="Name"
                    value={driverFormData.name}
                    onChange={handleDriverChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phone_number"
                    label="Phone Number"
                    value={driverFormData.phone_number}
                    onChange={handleDriverChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={driverFormData.status}
                      onChange={handleDriverChange}
                      label="Status"
                    >
                      <MenuItem value="idle">Idle</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="refueling">Refueling</MenuItem>
                      <MenuItem value="not_working">Not Working</MenuItem>
                      <MenuItem value="license_suspended">License Suspended</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Current Vehicle</InputLabel>
                    <Select
                      name="current_vehicle"
                      value={driverFormData.current_vehicle}
                      onChange={handleDriverChange}
                      label="Current Vehicle"
                      disabled={driverFormData.status === 'not_working' || driverFormData.status === 'license_suspended'}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {vehicles.map((vehicle) => (
                        <MenuItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(driverFormData.status === 'not_working' || driverFormData.status === 'license_suspended') && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Vehicle cannot be assigned when status is "{driverFormData.status === 'not_working' ? 'Not Working' : 'License Suspended'}"
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    License Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="license_number"
                    label="License Number"
                    value={driverFormData.license_number}
                    onChange={handleDriverChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="license_expiration_date"
                    label="License Expiration Date"
                    type="date"
                    value={driverFormData.license_expiration_date}
                    onChange={handleDriverChange}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>License State</InputLabel>
                    <Select
                      name="license_state"
                      value={driverFormData.license_state}
                      onChange={handleDriverChange}
                      label="License State"
                    >
                      <MenuItem value="">
                        <em>Select State</em>
                      </MenuItem>
                      <MenuItem value="AL">AL - Alabama</MenuItem>
                      <MenuItem value="AK">AK - Alaska</MenuItem>
                      <MenuItem value="AZ">AZ - Arizona</MenuItem>
                      <MenuItem value="AR">AR - Arkansas</MenuItem>
                      <MenuItem value="CA">CA - California</MenuItem>
                      <MenuItem value="CO">CO - Colorado</MenuItem>
                      <MenuItem value="CT">CT - Connecticut</MenuItem>
                      <MenuItem value="DE">DE - Delaware</MenuItem>
                      <MenuItem value="FL">FL - Florida</MenuItem>
                      <MenuItem value="GA">GA - Georgia</MenuItem>
                      <MenuItem value="HI">HI - Hawaii</MenuItem>
                      <MenuItem value="ID">ID - Idaho</MenuItem>
                      <MenuItem value="IL">IL - Illinois</MenuItem>
                      <MenuItem value="IN">IN - Indiana</MenuItem>
                      <MenuItem value="IA">IA - Iowa</MenuItem>
                      <MenuItem value="KS">KS - Kansas</MenuItem>
                      <MenuItem value="KY">KY - Kentucky</MenuItem>
                      <MenuItem value="LA">LA - Louisiana</MenuItem>
                      <MenuItem value="ME">ME - Maine</MenuItem>
                      <MenuItem value="MD">MD - Maryland</MenuItem>
                      <MenuItem value="MA">MA - Massachusetts</MenuItem>
                      <MenuItem value="MI">MI - Michigan</MenuItem>
                      <MenuItem value="MN">MN - Minnesota</MenuItem>
                      <MenuItem value="MS">MS - Mississippi</MenuItem>
                      <MenuItem value="MO">MO - Missouri</MenuItem>
                      <MenuItem value="MT">MT - Montana</MenuItem>
                      <MenuItem value="NE">NE - Nebraska</MenuItem>
                      <MenuItem value="NV">NV - Nevada</MenuItem>
                      <MenuItem value="NH">NH - New Hampshire</MenuItem>
                      <MenuItem value="NJ">NJ - New Jersey</MenuItem>
                      <MenuItem value="NM">NM - New Mexico</MenuItem>
                      <MenuItem value="NY">NY - New York</MenuItem>
                      <MenuItem value="NC">NC - North Carolina</MenuItem>
                      <MenuItem value="ND">ND - North Dakota</MenuItem>
                      <MenuItem value="OH">OH - Ohio</MenuItem>
                      <MenuItem value="OK">OK - Oklahoma</MenuItem>
                      <MenuItem value="OR">OR - Oregon</MenuItem>
                      <MenuItem value="PA">PA - Pennsylvania</MenuItem>
                      <MenuItem value="RI">RI - Rhode Island</MenuItem>
                      <MenuItem value="SC">SC - South Carolina</MenuItem>
                      <MenuItem value="SD">SD - South Dakota</MenuItem>
                      <MenuItem value="TN">TN - Tennessee</MenuItem>
                      <MenuItem value="TX">TX - Texas</MenuItem>
                      <MenuItem value="UT">UT - Utah</MenuItem>
                      <MenuItem value="VT">VT - Vermont</MenuItem>
                      <MenuItem value="VA">VA - Virginia</MenuItem>
                      <MenuItem value="WA">WA - Washington</MenuItem>
                      <MenuItem value="WV">WV - West Virginia</MenuItem>
                      <MenuItem value="WI">WI - Wisconsin</MenuItem>
                      <MenuItem value="WY">WY - Wyoming</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="license_class"
                    label="License Class"
                    value={driverFormData.license_class}
                    onChange={handleDriverChange}
                    required
                    fullWidth
                    placeholder="e.g., Class A, B, C, CDL"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 600 }}>
                    License Verification (Manager Only)
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={driverFormData.license_verified}
                        onChange={(e) => handleDriverChange({ target: { name: 'license_verified', value: e.target.checked } })}
                      />
                    }
                    label="License Verified"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Check this box to mark the license as verified. This will automatically record who verified it and when.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="license_verification_notes"
                    label="Verification Notes"
                    value={driverFormData.license_verification_notes}
                    onChange={handleDriverChange}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add any notes about the license verification..."
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDriverDialog}>Cancel</Button>
            <Button onClick={handleDriverSubmit} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Driver Detail View Dialog - Same as Dashboard */}
        <Dialog 
          open={driverDetailDialogOpen} 
          onClose={() => setDriverDetailDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Driver Details - {selectedDriver?.name || 'N/A'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setDriverDetailDialogOpen(false);
                  handleOpenDriverDialog(selectedDriver);
                }}
              >
                Edit
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedDriver && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar
                        src={getDriverProfilePictureUrl(selectedDriver.id, selectedDriver.profile_photo)}
                        alt={selectedDriver.name}
                        sx={{ width: 100, height: 100 }}
                      >
                        {selectedDriver.name?.charAt(0)?.toUpperCase() || 'D'}
                      </Avatar>
                      <Box>
                        <Typography variant="h5">{selectedDriver.name || 'N/A'}</Typography>
                        <Chip
                          label={selectedDriver.status || 'unknown'}
                          color={getStatusColor(selectedDriver.status)}
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                      Basic Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{selectedDriver.name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                    <Typography variant="body1">{selectedDriver.phone_number || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedDriver.email || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box>
                      <Chip
                        label={selectedDriver.status || 'unknown'}
                        color={getStatusColor(selectedDriver.status)}
                        size="small"
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}>
                      License Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">License Number</Typography>
                    <Typography variant="body1">{selectedDriver.license_number || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">License State</Typography>
                    <Typography variant="body1">{selectedDriver.license_state || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">License Class</Typography>
                    <Typography variant="body1">{selectedDriver.license_class || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">License Expiration Date</Typography>
                    <Typography variant="body1">
                      {selectedDriver.license_expiration_date 
                        ? new Date(selectedDriver.license_expiration_date).toLocaleDateString()
                        : 'N/A'}
                    </Typography>
                    {selectedDriver.license_expiration_date && (
                      <Typography variant="caption" color={
                        new Date(selectedDriver.license_expiration_date) < new Date() ? 'error' : 
                        new Date(selectedDriver.license_expiration_date) < new Date(Date.now() + 30*24*60*60*1000) ? 'warning.main' : 
                        'text.secondary'
                      }>
                        {new Date(selectedDriver.license_expiration_date) < new Date() 
                          ? '⚠️ Expired' 
                          : new Date(selectedDriver.license_expiration_date) < new Date(Date.now() + 30*24*60*60*1000)
                          ? '⚠️ Expires Soon'
                          : 'Valid'}
                      </Typography>
                    )}
                  </Grid>

                  {/* License Verification Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}>
                      License Verification
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Verification Status</Typography>
                    <Box>
                      <Chip
                        label={selectedDriver.license_verified ? 'Verified' : 'Not Verified'}
                        color={selectedDriver.license_verified ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  {selectedDriver.license_verified && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Verified By</Typography>
                        <Typography variant="body1">
                          {selectedDriver.license_verified_by_name || selectedDriver.license_verified_by?.email || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Verified At</Typography>
                        <Typography variant="body1">
                          {selectedDriver.license_verified_at 
                            ? new Date(selectedDriver.license_verified_at).toLocaleString()
                            : 'N/A'}
                        </Typography>
                      </Grid>
                      {selectedDriver.license_verification_notes && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Verification Notes</Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {selectedDriver.license_verification_notes}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}>
                      Vehicle Assignment
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {selectedDriver.current_vehicle_info ? (
                      <Box>
                        <Typography variant="body1" gutterBottom>
                          <strong>Assigned Vehicle:</strong> {selectedDriver.current_vehicle_info.display || 
                            `${selectedDriver.current_vehicle_info.make} ${selectedDriver.current_vehicle_info.model} (${selectedDriver.current_vehicle_info.license_plate})`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Vehicle ID: {selectedDriver.current_vehicle_info.id}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        No vehicle assigned
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}>
                      Current Job
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {selectedDriver.current_job_info ? (
                      <Box>
                        <Typography variant="body1" gutterBottom>
                          <strong>Job Location:</strong> {selectedDriver.current_job_info.job_location || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Diesel: {selectedDriver.current_job_info.diesel_quantity || '0'}L
                          {selectedDriver.current_job_info.def_quantity > 0 && 
                            ` • DEF: ${selectedDriver.current_job_info.def_quantity}L`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Job ID: {selectedDriver.current_job_info.id}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        No active job
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}>
                      Account Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Driver ID</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {selectedDriver.id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">User ID</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {selectedDriver.user?.id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Created At</Typography>
                    <Typography variant="body1">
                      {selectedDriver.created_at 
                        ? new Date(selectedDriver.created_at).toLocaleString()
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                    <Typography variant="body1">
                      {selectedDriver.updated_at 
                        ? new Date(selectedDriver.updated_at).toLocaleString()
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDriverDetailDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                setDriverDetailDialogOpen(false);
                handleOpenDriverDialog(selectedDriver);
              }}
            >
              Edit Driver
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={grantDialogOpen} onClose={handleCloseGrantDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Grant portal access</DialogTitle>
          <DialogContent>
            {grantTargetDriver && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  User: {grantTargetDriver.user?.email || grantTargetDriver.email || '—'} (
                  {grantTargetDriver.name})
                </Typography>
                {grantError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGrantError('')}>
                    {grantError}
                  </Alert>
                )}
                {grantSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {grantSuccess}
                  </Alert>
                )}
                <FormControl fullWidth margin="normal">
                  <InputLabel id="grant-role-label">Role to add</InputLabel>
                  <Select
                    labelId="grant-role-label"
                    label="Role to add"
                    value={grantRole}
                    onChange={(e) => setGrantRole(e.target.value)}
                  >
                    {!(grantTargetDriver.user?.accessible_roles || []).includes('customer') && (
                      <MenuItem value="customer">Customer portal</MenuItem>
                    )}
                    {!(grantTargetDriver.user?.accessible_roles || []).includes('driver') && (
                      <MenuItem value="driver">Driver portal (new profile)</MenuItem>
                    )}
                  </Select>
                </FormControl>
                {(grantTargetDriver.user?.accessible_roles || []).includes('customer') &&
                  (grantTargetDriver.user?.accessible_roles || []).includes('driver') && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      This user already has customer and driver access.
                    </Alert>
                  )}
                {grantRole === 'customer' && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Company name (optional)"
                        value={grantForm.company_name}
                        onChange={(e) => setGrantForm((f) => ({ ...f, company_name: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone (optional)"
                        value={grantForm.phone_number}
                        onChange={(e) => setGrantForm((f) => ({ ...f, phone_number: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address (optional)"
                        value={grantForm.address}
                        onChange={(e) => setGrantForm((f) => ({ ...f, address: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                )}
                {grantRole === 'driver' && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={grantForm.name}
                        onChange={(e) => setGrantForm((f) => ({ ...f, name: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={grantForm.phone_number}
                        onChange={(e) => setGrantForm((f) => ({ ...f, phone_number: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="License number"
                        value={grantForm.license_number}
                        onChange={(e) => setGrantForm((f) => ({ ...f, license_number: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        type="date"
                        label="License expiration"
                        InputLabelProps={{ shrink: true }}
                        value={grantForm.license_expiration_date}
                        onChange={(e) =>
                          setGrantForm((f) => ({ ...f, license_expiration_date: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="License state"
                        value={grantForm.license_state}
                        onChange={(e) => setGrantForm((f) => ({ ...f, license_state: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="License class"
                        value={grantForm.license_class}
                        onChange={(e) => setGrantForm((f) => ({ ...f, license_class: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseGrantDialog} disabled={grantLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGrantPortalAccess}
              disabled={grantLoading}
              startIcon={grantLoading ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {grantLoading ? 'Saving…' : 'Grant access'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ManagerDrivers;
